import { Component, OnInit } from '@angular/core';
import { AppComponentName, MessageActionType, MessageType, Message, UserGroup, UserInfos } from '../../shared/models';
import { AppMessageService } from '../../shared/services/message.service';
import { UserService } from '../../shared/services/user.service';
import { AppComponent } from '../../app.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { CommonShared } from 'app/shared/classes/common';
import { UserDetailsComponent, UserDetailsParams } from './user-details/user-details.component';
import { ChangeLogNotesComponent, ChangeLogNotesParams } from '../messages/changelog-notes/changelognotes.components';
import { forkJoin } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import moment from 'moment';

@Component({
  standalone: false,
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnInit {

  public common = new CommonShared();

  modalDragOn: boolean = false;

  public username: string = "unknown";
  public currentUser: UserInfos;
  public userInfos: UserInfos[] = [];
  public awaitUsers: boolean = false;
  public userEdited: boolean = false;
  public userIsAdmin: boolean = false;
  public adminGroup: UserGroup;
  public userGroups: UserGroup[] = [];
  public awaitUserGroups: boolean = false;
  public associatedUserGroups: UserGroup[] = [];
  public preEditObjectAsJSONString = '';
  public changeLogNotes = '';

  public userCols = [
    {field: 'userName', header: 'User', width: '25%'},
    {field: 'lastSiteLoadDate', header: 'Last Site Load Date', width: '25%'},
    {field: 'createdDate', header: 'Created Date', width: '25%'},
    {field: 'groupList', header: 'Groups', width: '15%'},
    {field: 'userDetails', header: 'Details', width: '5%'},
    {field: 'delete', header: 'Delete', width: '5%'}
  ];
  

  constructor(public toastr: ToastrService, public modalService: BsModalService, private confirmationService: ConfirmationService,
              public _userService: UserService, private global: AppComponent, public _appMessageService: AppMessageService) { }

  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {

        this.username = results["value"];
        if (this.username != null && this.username != '') {
          this.startUp()
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    this.getAllUsers();
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  getAllUsers(): void {
    // this._userService.getAllUsers().subscribe(
    this._userService.getAllUsersWithStatus().subscribe(
      results => {
        if (results != null) {
          this.awaitUsers = true;
          this.userInfos = results;
          // Dates come from the API as UTC (no timezone suffix); parse as UTC so the date pipe renders them in local time.
          this.userInfos.forEach(u => {
            if (u.createdDate) { u.createdDate = moment.utc(u.createdDate).toDate(); }
            if (u.lastSiteLoadDate) { u.lastSiteLoadDate = moment.utc(u.lastSiteLoadDate).toDate(); }
          });
          this.common.sortArrayByUserNameParm(this.userInfos);
          this.getUserGroups();
        } else {
          this.toastr.warning('No users found');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving user list');
      });
  }


  getUserGroups(): void {
    this._userService.getUserGroups().subscribe(
      results => {
        if (results != null) {
          this.awaitUserGroups = true;
          this.userGroups = results;
          this.userInfos.forEach(g => this.buildGroupList(g));

          this.adminGroup = this.userGroups.find(x => x.name.toLowerCase() == "admins");
          if (this.adminGroup != undefined) {
            this.userIsAdmin = (this.userInfos.find(u => u.userName.toLowerCase() === this.username.toLowerCase()) != undefined) ? true : false;
          }

        }
        else {
          this.toastr.warning('No User Groups found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving current display.');
      });
  }


  buildGroupList(userInfo: UserInfos) {
    userInfo.groupListCount = 0;
    userInfo.groupList = "";

    this.userGroups.forEach(g => {
      var user = g.userInfos.find(u => u.userName.toLowerCase() == userInfo.userName.toLowerCase());
      if (user != undefined) {
        userInfo.groupList += g.name + "\n";
        userInfo.groupListCount++;
      }
    });
  }

  
  openUserDetails(userInfo: UserInfos) {
    const params: UserDetailsParams = {
      userInfo: userInfo,
      userIsAdmin: this.userIsAdmin,
      allUserGroups: this.userGroups,
      adminUserGroup: this.userGroups.find(x => x.name.toLowerCase() == "admins")
    };

    this.modalService.show(UserDetailsComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, initialState: params })
      .onHide
      .subscribe(() => this.getUserGroups());
  }


  onDeleteConfirm(userInfo: UserInfos) {
    this.confirmationService.confirm({
      message: "Please confirm that you want to delete user '" + userInfo.userName + ".'<br>This user will be removed from all groups and items owned by this user will be switched to the 'Admins' group.",
      key: 'cd',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.startUserDeletion(userInfo);
      },
      reject: () => {
      }
    });
  }


  startUserDeletion(userInfo: UserInfos) {
    this.currentUser = userInfo;

    // Move the user's self-owned items to Admins and update group memberships, then delete the user
    // only after ALL of those operations complete (forkJoin). Replaces the previous manual
    // job-counter coordination, which was racy and could leave the delete step un-triggered.
    this._userService.getAssociatedUserGroups(userInfo.userName).pipe(
      switchMap(groups => {
        userInfo.associatedUserGroups = groups ?? [];
        const groupsToUpdate = this.prepareAssociatedGroupUpdates(userInfo);
        this.buildGroupList(userInfo);

        // The replace observable is always present, so the array is never empty (forkJoin([]) never emits).
        return forkJoin([
          this._userService.replaceUserOwnedItemsWithGroup(userInfo.id, this.adminGroup.id),
          ...groupsToUpdate.map(g => this._userService.updateUserGroup(g))
        ]);
      }),
      switchMap(results => {
        const replaceResult = results[0];         // replaceUserOwnedItemsWithGroup: null == success
        const groupResults = results.slice(1);    // updateUserGroup: non-null == success

        if (replaceResult != null) {
          throw new Error('self-owned items were not moved: ' + replaceResult);
        }
        if (groupResults.some(r => r == null)) {
          throw new Error('a group membership was not updated');
        }

        this.toastr.success('Success: ' + userInfo.userName + ' self-owned items switched to ' + this.adminGroup.name);
        return this._userService.deleteUser(userInfo.id);
      })
    ).subscribe({
      next: deleteResult => {
        if (deleteResult == null) {
          this.toastr.success('Success: ' + userInfo.userName + ' deleted');
          this.createMessageData(MessageActionType.Deleted, userInfo, 'User Deleted');

          var userIndex = this.userInfos.findIndex(x => x.id == userInfo.id);
          if (userIndex != -1) {
            this.userInfos.splice(userIndex, 1);
            this.userInfos = [...this.userInfos];
          }
          this.currentUser = undefined;
        }
        else {
          this.toastr.error('Warning deleting user: ' + deleteResult);
        }
      },
      error: () => {
        this.toastr.error('Error occurred while deleting user: ' + userInfo.userName);
      }
    });
  }


  private prepareAssociatedGroupUpdates(userInfo: UserInfos): UserGroup[] {
    const groupsToUpdate: UserGroup[] = [];

    userInfo.associatedUserGroups.forEach(x => {
      if (x.userInfos.length < 2) {                                             // add the Admins users to the group if no one will be left in group
        this.adminGroup.userInfos.forEach(y => {
          var adminUserIndex = x.userInfos.findIndex(z => z.id == y.id);        // Don't add admin if already in the group
          if (adminUserIndex == -1) {
            x.userInfos.push(y);
          }
        }); 
      }
      
      var userIndexInGroup = x.userInfos.findIndex(y => y.id == userInfo.id);   // find and remove user from current group if others in group
      if (userIndexInGroup != -1) {
        x.userInfos.splice(userIndexInGroup, 1);
      }

      if (x.name != 'Self') {  // Don't update the 'Self' group since it is a virtual group
        groupsToUpdate.push(x);
      }
    });

    return groupsToUpdate;
  }


  addNotes2ChangeLog() {
    this.changeLogNotes = '';
    
    const params: ChangeLogNotesParams = {
      changeLogNotes: this.changeLogNotes,
      userGroupId: 0,
      category: 'Users'
    };

    var modalRef = this.modalService.show(ChangeLogNotesComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, class: 'modal-above-confirmdialog', initialState: params });
    modalRef.content.onChangeLogNotes.subscribe((value) => {
      this.changeLogNotes = value;
    });
  }

  

  createMessageData(msgActionType: MessageActionType, userInfo: UserInfos, msgNote: string) {
    const message: Message = {
      id: 0,
      messageType: MessageType.Audit,
      appComponentName: AppComponentName.Users,
      actionType: msgActionType,
      appComponentItem: userInfo.userName,
      details: msgNote,
      userNotes: this.changeLogNotes,
      count: 1,
      acknowledged: false,
      userName: this.username,
      userGroupId: null
    };

    this._appMessageService.createAppMessage(message).subscribe(
      results => {
        if (results != null) {
          //this.toastr.success('Success');
        } else {
          //this.toastr.warning('Not found.');
        }
        return;
      },
      err => {
        //this.toastr.error('Error occurred while saving change log.');
      });
  }







}
